import { BRAND } from "constants/brand";
import { Ring } from "@uiball/loaders";
import "assets/styles/table.scss";
import { useDispatch, useSelector } from "react-redux";
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";
import { useState, useRef, useEffect, useCallback } from "react";
import { Toast } from "primereact/toast";
import { useQueryClient } from "@tanstack/react-query";
import useUser, { useGetUsers, userKeys } from "hooks/useUser";
import {
    updateDeliveryWalletMode,
    updateDeliveryOrderLimit,
    updateDeliveryShippingPercentage,
} from "services/user";
import { setPage } from "store/userSlice";
import DeleteItem from "components/Ui/DeleteItem/DeleteItem";
import User from "./User/User";
import { useNavigate, useSearchParams } from "react-router-dom";
import SearchInput from "components/Ui/SearchInput/SearchInput";
import AddUser from "./AddUser/AddUser";
import EditUser from "./EditUser/EditUser";
import ChangePassword from "./ChangePassword/ChangePassword";
import DriverWallet from "./DriverWallet/DriverWallet";
import { Form } from "react-bootstrap";
import useCity from "hooks/useCity";
import AddAreas from "./EditUser/AddArea";
import { api } from "api";
import { impersonateUser as impersonateUserURL } from "api/apiURLs";
import { authSliceActions } from "store/authSlice";
import { getStoredToken, getStoredRefreshToken } from "utils/auth";
import TableColumnsMenu from "components/Ui/TableColumnsMenu/TableColumnsMenu";
import TableHead from "components/Ui/TableHead/TableHead";
import ListLoadError from "components/Ui/ListLoadError/ListLoadError";
import useTableColumnsConfig from "hooks/useTableColumnsConfig";
import { ROLE_LABELS, ROLE_DEFINITE_LABELS, ROLE_PLURAL_LABELS } from "constants/roleLabels";

/**
 * The shared table shell uses a separate column set for each user role.
 *
 * The API still uses one role-filtered list request. The page decides which
 * fields and actions are useful for the selected role.
 */
/**
 * ONE COLUMN ORDER FOR EVERY ACCOUNT TABLE, in five groups:
 *
 *   1. identity      — select, #, name
 *   2. classification— what kind of account this is
 *   3. place         — city, areas, address
 *   4. live state    — is the app open right now
 *   5. track record  — orders, rating, performance
 *   6. account state — blocked or not
 *   7. actions       — always last
 *
 * The point is not the grouping itself but that it is the SAME on all four
 * pages, restaurants included. An operator moving between them finds the city
 * in the same place and the block control in the same place, instead of
 * relearning each table. Columns unique to a role slot into the group they
 * belong to rather than onto the end.
 *
 * Add each new column inside its semantic group. Do not append a new column
 * after الحظر or الاعدادات because it is the newest field.
 *
 * Anyone who has already reordered their own columns keeps their layout —
 * `useTableColumnsConfig` stores it — and reaches this order with the reset in
 * the columns menu.
 */
const COMMON_COLUMNS = [
    { id: "select", label: "", width: "30px", pinned: true },
    { id: "id", label: "#", width: "10px" },
    { id: "name", label: "الاسم", width: "100px" },
    { id: "city", label: "المدينه", width: "80px" },
    { id: "block", label: "الحظر", width: "100px" },
    { id: "actions", label: "الاعدادات", width: "50px" },
];

/**
 * Which headers can be clicked to sort.
 *
 * Exactly the API's allow-list in util/listSort.js, spelled the same way. The
 * tables are paginated, so a header that sorted only the ten rows on screen
 * would reorder a page rather than the list — worse than not offering it.
 *
 * The completed-order counts are NOT here. The API computes them from a
 * separate aggregate over the ids on the page, so it cannot order by one.
 */
const SORTABLE_COLUMNS = ["id", "name", "createdAt"];

const LEGACY_DELIVERY_COLUMN_ORDER = [
    "select",
    "id",
    "name",
    "commissionPercentage",
    "city",
    "areasCount",
    "appStatus",
    "completedOrdersDelivered",
    "rating",
    "walletBalance",
    "block",
    "performance",
    "maxConcurrentOrders",
    "actions",
];

const USER_ROLE_CONFIGS = {
    delivery: {
        emptyMessage: `لا يوجد ${ROLE_PLURAL_LABELS.delivery}`,
        columnKey: "deliveries",
        apiRole: "delivery",
        showAppStatusFilter: true,
        allowCreate: true,
        addLabel: `إضافة ${ROLE_LABELS.delivery}`,
        allowCopyAreas: true,
        fixedRole: "delivery",
        allowedRoles: ["delivery"],
        legacyDefaultOrders: [LEGACY_DELIVERY_COLUMN_ORDER],
        columns: [
            COMMON_COLUMNS[0],
            COMMON_COLUMNS[1],
            COMMON_COLUMNS[2],
            // AGREEMENT: the rate is a rule for this driver, not a result.
            { id: "commissionPercentage", label: "النسبة", width: "120px" },
            // PLACE: show the driver's service area before live state.
            COMMON_COLUMNS[3],
            { id: "areasCount", label: "عدد المناطق", width: "80px" },
            // LIVE STATE: show whether the driver app can receive work.
            { id: "appStatus", label: "تطبيق السائق", width: "140px" },
            // TRACK RECORD: show completed work, rating, and recent performance.
            { id: "completedOrdersDelivered", label: "طلبات موصّلة", width: "100px" },
            { id: "rating", label: `تقييم ${ROLE_DEFINITE_LABELS.delivery}`, width: "90px" },
            {
                id: "performance",
                label: "أداء السائق (30 يوم)",
                hint: "القبول: متوسط الوقت من إنشاء الطلب حتى ضغط السائق زر «قبول». الاستلام: نسبة الطلبات التي استلمها السائق من المطعم قبل انتهاء وقت التحضير.",
                width: "220px",
            },
            // WORK LIMITS: keep the ceiling and wallet beside the block control.
            { id: "maxConcurrentOrders", label: "حد الطلبات", width: "90px" },
            // Whether he pays per order at all, then how much he has. The two
            // read together: a driver on the wallet with a 0 balance takes no
            // orders, and that is only obvious when the mode sits beside it.
            { id: "walletMode", label: "المحفظة", width: "110px" },
            { id: "walletBalance", label: "الرصيد", width: "100px" },
            COMMON_COLUMNS[4],
            // ACTIONS always stays last.
            COMMON_COLUMNS[5],
        ],
    },
    customer: {
        emptyMessage: `لا يوجد ${ROLE_PLURAL_LABELS.customer}`,
        columnKey: "customers",
        apiRole: "customer",
        showAppStatusFilter: false,
        showNoteFilter: true,
        allowCreate: false,
        allowCopyAreas: false,
        columns: [
            COMMON_COLUMNS[0],
            COMMON_COLUMNS[1],
            COMMON_COLUMNS[2],
            COMMON_COLUMNS[3],
            { id: "address", label: "العنوان", width: "100px" },
            { id: "completedOrdersMade", label: "طلبات مكتملة", width: "100px" },
            COMMON_COLUMNS[4],
            COMMON_COLUMNS[5],
        ],
    },
    admin: {
        emptyMessage: `لا يوجد ${ROLE_PLURAL_LABELS.admin}`,
        columnKey: "admins",
        // Two values: one account carries users.role = "super_admin" and would
        // be invisible to a page asking only for "admin". See api b0de0a7.
        apiRole: "admin,super_admin",
        showAdminTypeFilter: true,
        showAppStatusFilter: false,
        allowCreate: true,
        addLabel: `إضافة ${ROLE_LABELS.admin}`,
        allowCopyAreas: false,
        fixedRole: null,
        allowedRoles: ["admin", "super_admin"],
        columns: [
            COMMON_COLUMNS[0],
            COMMON_COLUMNS[1],
            COMMON_COLUMNS[2],
            // A super admin is not scoped to a city and can reach the force-update
            // lever, so which kind of admin a row is matters more than anything
            // else on it. It sits in the classification slot, where the
            // restaurants page carries النوع.
            { id: "role", label: "نوع المشرف", width: "100px" },
            COMMON_COLUMNS[3],
            COMMON_COLUMNS[4],
            COMMON_COLUMNS[5],
        ],
    },
};

const Users = ({ role = "customer" }) => {
    const roleConfig = USER_ROLE_CONFIGS[role] || USER_ROLE_CONFIGS.customer;
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [showCopyAreas, setShowCopyAreas] = useState(false);

    const [showAddForm, setShowAddForm] = useState(false);
    const [showEditForm, setShowEditForm] = useState(false);
    const [showDelete, setShowDelete] = useState(false);
    const [showChangePassword, setShowChangePassword] = useState(false);
    const [showWallet, setShowWallet] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const { deleteUser, toggleBlockUser } = useUser(roleConfig.apiRole);
    const dispatch = useDispatch();
    const toast = useRef(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const { cities } = useCity();
    const adminInfo = useSelector((state) => state.auth.userData);
    const page = useSelector((state) => state.user.page);
    const currentPage = page;
    const queryClient = useQueryClient();

    // One cache entry per role, so a route change cannot paint the previous
    // page's accounts and a page already visited paints from cache. `loading`
    // is true only when there is nothing at all to show, so a background
    // refetch never blanks the table or unmounts the search box.
    const {
        users: data,
        pages,
        loading,
        isFetching,
        loadError,
        refetch,
    } = useGetUsers({
        role: roleConfig.apiRole,
        page,
        size: 10,
        search: searchParams.get("search") || "",
        appStatus: roleConfig.showAppStatusFilter ? searchParams.get("appStatus") || "" : "",
        cityId: searchParams.get("cityId") || "",
        hasNote: searchParams.get("hasNote") || "",
        sortBy: searchParams.get("sortBy") || "",
        sortDir: searchParams.get("sortDir") || "",
        adminType: roleConfig.showAdminTypeFilter ? searchParams.get("adminType") || "" : "",
    });

    // Sort and filters live in the URL, not in component state, so a sorted or
    // filtered list survives a reload and can be sent to someone else as a link.
    const sort = {
        by: searchParams.get("sortBy") || "",
        dir: searchParams.get("sortDir") || "asc",
    };

    const updateParams = useCallback(
        (changes) => {
            const next = new URLSearchParams(searchParams);
            Object.entries(changes).forEach(([key, value]) => {
                if (value) next.set(key, value);
                else next.delete(key);
            });
            setSearchParams(next);
            // Any change to what is being listed invalidates the page number:
            // page 4 of one filter is not page 4 of another.
            dispatch(setPage(1));
        },
        [searchParams, setSearchParams, dispatch],
    );

    /** After anything that changes an account, ask for this role's list again. */
    const refreshList = useCallback(
        () => queryClient.invalidateQueries({ queryKey: userKeys.all }),
        [queryClient],
    );
    const { orderedColumns, visibleColumns, hiddenSet, toggleColumn, moveColumn, reset } =
        useTableColumnsConfig(roleConfig.columnKey, roleConfig.columns, {
            legacyDefaultOrders: roleConfig.legacyDefaultOrders,
        });

    // Page 1 on every role change. Without it, opening the drivers page while
    // on page 4 of the customers asks for a page of drivers that may not exist.
    useEffect(() => {
        dispatch(setPage(1));
    }, [dispatch, role]);

    const handleChangePage = (event, newPage) => {
        dispatch(setPage(newPage));
    };

    const showMessage = (type, head, content) => {
        toast.current.show({
            severity: type,
            summary: head,
            detail: content,
            life: 3000,
        });
    };

    /**
     * Save one driver's commission rate from the table.
     *
     * `null` clears it, which puts him back on his city's default — a real
     * action, not an error, so an empty box is allowed.
     */
    const handleSaveDriverPercentage = useCallback(
        async (user, percentage) => {
            try {
                await updateDeliveryShippingPercentage(user.id, percentage);
                showMessage(
                    "success",
                    "تم الحفظ",
                    percentage === null ? "أصبح السائق على نسبة المدينة" : "تم تحديث النسبة",
                );
                await queryClient.invalidateQueries({ queryKey: userKeys.all });
            } catch (err) {
                showMessage(
                    "error",
                    "تعذر الحفظ",
                    err.response?.data?.message || "حدث خطأ غير متوقع",
                );
            }
        },
        [queryClient],
    );

    /** Save or clear one driver's order ceiling from the table. */
    const handleSaveDriverOrderLimit = useCallback(
        async (user, maxConcurrentOrders) => {
            try {
                await updateDeliveryOrderLimit(user.id, maxConcurrentOrders);
                showMessage(
                    "success",
                    "تم الحفظ",
                    maxConcurrentOrders === null ? "تم إلغاء حد الطلبات" : "تم تحديث حد الطلبات",
                );
                await queryClient.invalidateQueries({ queryKey: userKeys.all });
            } catch (err) {
                showMessage(
                    "error",
                    "تعذر الحفظ",
                    err.response?.data?.message || "حدث خطأ غير متوقع",
                );
                throw err;
            }
        },
        [queryClient],
    );

    /**
     * Put one driver on the wallet, or take him off it (api#137).
     *
     * The message names the model rather than saying "saved", because the two
     * consequences are different and the operator has to know which one he just
     * chose: a wallet driver is debited on every order he accepts and takes
     * none at all on a zero balance.
     */
    const handleSaveDriverWalletMode = useCallback(
        async (user, walletEnabled) => {
            try {
                await updateDeliveryWalletMode(user.id, walletEnabled);
                showMessage(
                    "success",
                    "تم الحفظ",
                    walletEnabled
                        ? "السائق على المحفظة — يخصم من رصيده مع كل طلب"
                        : "السائق نقدي — يدفع في آخر الشهر",
                );
                await queryClient.invalidateQueries({ queryKey: userKeys.all });
            } catch (err) {
                showMessage(
                    "error",
                    "تعذر الحفظ",
                    err.response?.data?.message || "حدث خطأ غير متوقع",
                );
                throw err;
            }
        },
        [queryClient],
    );

    /**
     * Driver app presence. This filter appears only on the delivery page.
     * "online_dark" means that a driver declared availability and then went
     * quiet.
     */
    const handleAppStatusChange = (value) => {
        if (value) {
            searchParams.set("appStatus", value);
        } else {
            searchParams.delete("appStatus");
        }
        setSearchParams(searchParams);
        dispatch(setPage(1));
    };

    const handleSearch = (value) => {
        if (value) {
            searchParams.set("search", value);
        } else {
            searchParams.delete("search");
        }
        setSearchParams(searchParams);
        dispatch(setPage(1));
    };

    const toggleAddForm = () => {
        setShowAddForm((pre) => (pre = !pre));
    };

    const toggleEditForm = () => {
        setShowEditForm((pre) => (pre = !pre));
    };

    const setSelectedUserValue = (vendor) => {
        setSelectedUser(vendor);
    };

    const toggleDelete = () => {
        setShowDelete((pre) => (pre = !pre));
    };

    const toggleChangePassword = () => {
        setShowChangePassword((pre) => (pre = !pre));
    };

    // Every role goes through the same count-first path as the restaurants
    // screen and can come back archived instead of deleted, so the response
    // decides the wording — including the noun, since the API knows whether it
    // just removed a restaurant, a driver, an admin or a customer.
    const confirmDelete = async () => {
        try {
            const response = await deleteUser(selectedUser.id);
            const isArchived = response?.deleted === false;

            showMessage(
                isArchived ? "info" : "warn",
                isArchived ? "تم الإخفاء" : "تم الحذف",
                response?.message || "تم حذف الحساب بنجاح",
            );
            toggleDelete();
            refreshList();
        } catch (err) {
            console.error("Error deleting user:", err);
            showMessage("error", "هناك خطأ", err.response?.data?.message || "حدث خطأ غير متوقع");
        }
    };

    const handleImpersonate = async (user) => {
        try {
            const response = await api.post(`${impersonateUserURL}/${user.id}`, {
                supportsRefresh: true,
            });
            dispatch(
                authSliceActions.startImpersonation({
                    impersonatedUser: response.data.user,
                    originalToken: getStoredToken(),
                    originalRefreshToken: getStoredRefreshToken(),
                }),
            );
            showMessage("info", "انتحال هوية", `أنت الآن تتصفح كـ ${user.name}`);

            // Land on the home page rather than staying on /users. The user
            // list is an admin-only screen, so an admin who impersonates a
            // vendor from here is left staring at a page that impersonated
            // session has no business rendering — and whose next refetch runs
            // as the vendor. Home is the one route every role can open.
            navigate("/");
        } catch (err) {
            showMessage("error", "هناك خطأ", err.response?.data?.message || "حدث خطأ غير متوقع");
        }
    };

    const handleToggleBlock = async (user) => {
        try {
            const response = await toggleBlockUser(user.id);

            if (response) {
                // The same click restores a removed account and un-bans a
                // blocked one. The server knows which it just did — it returns
                // "تم إرجاع الحساب" for the first — so the heading follows the
                // row's own state rather than always saying "إلغاء الحظر".
                const wasArchived = Boolean(user?.archivedAt);

                showMessage(
                    response.user?.isBlocked ? "warn" : "success",
                    response.user?.isBlocked
                        ? "تم الحظر"
                        : wasArchived
                          ? "تم الإرجاع"
                          : "تم إلغاء الحظر",
                    response.message,
                );
                refreshList();
            } else {
                showMessage("error", "هناك خطأ", "حدث خطأ غير متوقع");
            }
        } catch (_err) {
            showMessage("error", "هناك خطأ", "حدث خطأ غير متوقع");
        }
    };

    return (
        <div className="items">
            <Toast style={{ direction: "rtl" }} ref={toast} position="top-left" />

            {/* `loading` is TanStack's isLoading: true only when this role has no
                cached data at all. A background refetch, a page change or a
                search keeps the table and the search box mounted, which is what
                the old hand-rolled hasLoaded flag was for. */}
            {loading ? (
                <div className="loadingSpinner">
                    <Ring size={40} lineWeight={5} speed={2} color={BRAND} />
                </div>
            ) : (
                <>
                    <div className="controls">
                        <div style={{ display: "flex", gap: "10px" }}>
                            {roleConfig.showAppStatusFilter ? (
                                <Form.Select
                                    value={searchParams.get("appStatus") || ""}
                                    onChange={(e) => handleAppStatusChange(e.target.value)}
                                    title="حالة تطبيق السائق"
                                >
                                    <option value={""}>التطبيق: الكل</option>
                                    <option value={"online_dark"}>متصل — التطبيق مغلق</option>
                                    <option value={"closed"}>التطبيق مغلق</option>
                                    <option value={"open"}>التطبيق مفتوح</option>
                                </Form.Select>
                            ) : null}
                            {/* Accounts an admin wrote a note about. Only 5 of
                                23,917 customers carry one, and there was no
                                other way to find them again. */}
                            {roleConfig.showNoteFilter ? (
                                <Form.Select
                                    value={searchParams.get("hasNote") || ""}
                                    onChange={(e) => updateParams({ hasNote: e.target.value })}
                                    title="الحسابات التي عليها ملاحظة"
                                >
                                    <option value={""}>الملاحظات: الكل</option>
                                    <option value={"1"}>عليها ملاحظة فقط</option>
                                </Form.Select>
                            ) : null}
                            {/* Which kind of admin. Reads admins.super_admin on
                                the server, which is the flag every guard checks
                                — not users.role, which disagrees with it. */}
                            {roleConfig.showAdminTypeFilter ? (
                                <Form.Select
                                    value={searchParams.get("adminType") || ""}
                                    onChange={(e) => updateParams({ adminType: e.target.value })}
                                    title="نوع المشرف"
                                >
                                    <option value={""}>النوع: الكل</option>
                                    <option value={"super"}>{ROLE_LABELS.super_admin}</option>
                                    <option value={"regular"}>{ROLE_LABELS.admin}</option>
                                </Form.Select>
                            ) : null}
                            {/* Every people page can be narrowed to one city.
                                A city admin is pinned server-side, so the
                                control is only useful — and only offered — to a
                                super admin. */}
                            {adminInfo?.super_admin ? (
                                <Form.Select
                                    value={searchParams.get("cityId") || ""}
                                    onChange={(e) => updateParams({ cityId: e.target.value })}
                                    title="المدينة"
                                >
                                    <option value={""}>المدينة: الكل</option>
                                    {cities?.map((city) => (
                                        <option key={city.id} value={city.id}>
                                            {city.name}
                                        </option>
                                    ))}
                                </Form.Select>
                            ) : null}
                            <SearchInput
                                initialValue={searchParams.get("search") || ""}
                                onSearch={handleSearch}
                                placeholder="بحث بالاسم، الهاتف أو رقم الحساب"
                                minWidth={340}
                            />
                        </div>
                        <div>
                            {roleConfig.allowCreate ? (
                                <button className="button" onClick={toggleAddForm}>
                                    {roleConfig.addLabel}
                                </button>
                            ) : null}
                            {roleConfig.allowCopyAreas ? (
                                <button
                                    className="button"
                                    style={{ opacity: selectedUsers.length === 0 ? 0.6 : 1 }}
                                    disabled={selectedUsers.length === 0}
                                    onClick={() => setShowCopyAreas(true)}
                                >
                                    تعيين المناطق
                                </button>
                            ) : null}
                        </div>
                        <div style={{ marginInlineStart: "auto" }}>
                            <TableColumnsMenu
                                orderedColumns={orderedColumns}
                                hiddenSet={hiddenSet}
                                toggleColumn={toggleColumn}
                                moveColumn={moveColumn}
                                reset={reset}
                            />
                        </div>
                    </div>
                    <div className="table-container">
                        <table>
                            <TableHead
                                visibleColumns={visibleColumns}
                                sortableColumns={SORTABLE_COLUMNS}
                                sort={sort}
                                onSortChange={(by, dir) =>
                                    updateParams({ sortBy: by, sortDir: dir })
                                }
                                headerRenderers={{
                                    select: () => (
                                        <input
                                            type="checkbox"
                                            className="form-check-input"
                                            checked={
                                                data?.length > 0 &&
                                                data.every((v) => selectedUsers.includes(v.id))
                                            }
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setSelectedUsers((prev) => {
                                                        const pageIds = data.map((v) => v.id);
                                                        return Array.from(
                                                            new Set([...prev, ...pageIds]),
                                                        );
                                                    });
                                                } else {
                                                    setSelectedUsers((prev) => {
                                                        const pageIds = data.map((v) => v.id);
                                                        return prev.filter(
                                                            (id) => !pageIds.includes(id),
                                                        );
                                                    });
                                                }
                                            }}
                                        />
                                    ),
                                }}
                            />
                            {data.length ? (
                                <tbody>
                                    {data.map((user) => (
                                        <User
                                            key={user.id}
                                            user={user}
                                            visibleColumns={visibleColumns}
                                            setSelectedUser={setSelectedUser}
                                            toggleEditForm={toggleEditForm}
                                            setSelectedUserValue={setSelectedUserValue}
                                            selectedUsers={selectedUsers}
                                            setSelectedUsers={setSelectedUsers}
                                            toggleDelete={toggleDelete}
                                            toggleChangePassword={toggleChangePassword}
                                            onOpenWallet={
                                                roleConfig.apiRole === "delivery"
                                                    ? (u) => {
                                                          setSelectedUser(u);
                                                          setShowWallet(true);
                                                      }
                                                    : null
                                            }
                                            onSavePercentage={
                                                roleConfig.apiRole === "delivery"
                                                    ? handleSaveDriverPercentage
                                                    : null
                                            }
                                            onSaveOrderLimit={
                                                roleConfig.apiRole === "delivery"
                                                    ? handleSaveDriverOrderLimit
                                                    : null
                                            }
                                            onSaveWalletMode={
                                                roleConfig.apiRole === "delivery"
                                                    ? handleSaveDriverWalletMode
                                                    : null
                                            }
                                            onToggleBlock={handleToggleBlock}
                                            onImpersonate={handleImpersonate}
                                            showMessage={showMessage}
                                        />
                                    ))}
                                </tbody>
                            ) : loadError ? null : (
                                <h4>{roleConfig.emptyMessage}</h4>
                            )}
                        </table>
                        {/* A failed load is not an empty list. The page showed
                            «لا يوجد ...» for both. */}
                        {loadError && !data.length ? (
                            <ListLoadError
                                message={loadError}
                                onRetry={refetch}
                                isRetrying={isFetching}
                            />
                        ) : null}
                    </div>
                    <Stack spacing={1}>
                        <Pagination
                            count={pages}
                            defaultPage={currentPage}
                            shape="rounded"
                            color={BRAND}
                            style={{ margin: "20px auto 0" }}
                            onChange={handleChangePage}
                        />
                    </Stack>
                    <AddUser
                        show={showAddForm && roleConfig.allowCreate}
                        // Refreshes on cancel too. A refetch costs one request;
                        // a list that still lacks the account just added costs a
                        // support call.
                        close={() => {
                            toggleAddForm();
                            refreshList();
                        }}
                        showMessage={showMessage}
                        cities={cities}
                        fixedRole={roleConfig.fixedRole}
                        allowedRoles={roleConfig.allowedRoles}
                    />
                    <EditUser
                        show={showEditForm}
                        close={() => {
                            toggleEditForm();
                            refreshList();
                        }}
                        showMessage={showMessage}
                        user={selectedUser}
                        cities={cities}
                    />
                    <DeleteItem
                        show={showDelete}
                        close={toggleDelete}
                        loading={loading}
                        confirm={confirmDelete}
                        message="سيتم حذف الحساب نهائيا إذا لم يكن عليه أي سجل. إذا كان له طلبات أو كشوفات أو تقييمات، سيتم إخفاؤه مع الاحتفاظ بسجله، ويمكن إرجاعه من خلال إلغاء الحظر."
                    />
                    <DriverWallet
                        show={showWallet}
                        close={() => setShowWallet(false)}
                        showMessage={showMessage}
                        user={selectedUser}
                    />
                    <ChangePassword
                        show={showChangePassword}
                        close={toggleChangePassword}
                        showMessage={showMessage}
                        user={selectedUser}
                    />
                    <AddAreas
                        show={showCopyAreas && roleConfig.allowCopyAreas}
                        close={() => {
                            setShowCopyAreas(false);
                            refreshList();
                        }}
                        showMessage={showMessage}
                        selectedUsers={selectedUsers}
                        cities={cities}
                    />
                </>
            )}
        </div>
    );
};
export default Users;
